import { api, ORG_ROLES } from '../lib/api.js';
import { el, roleLabel } from '../lib/dom.js';
import { confirmDialog, openModal, roleChip } from '../lib/ui.js';
import { credentialsPanel } from './credentials-panel.js';

// ---------------------------------------------------------------------------
// Org-scoped actions on ONE membership — change role, resend invite,
// deactivate/reactivate in the org, remove from the org. Shared by an
// organisation's Members tab and a user's Organisations tab, so the same verb
// always carries the same wording and the same confirmation.
//
// Every confirmation names the organisation: these never touch the account or
// any other organisation the person belongs to.
// ---------------------------------------------------------------------------

/**
 * Menu items for membership m: { orgId, userId, email, fullName, role,
 * activated, membershipActive, removedAt }.
 *   onChanged(message) — after a successful change (caller toasts + reloads)
 *   onCredentials(node) — a fresh join link to show (resend invite)
 */
export function membershipActions(m, { orgName, onChanged, onCredentials }) {
  const org = orgName ?? 'this organisation';
  if (m.removedAt !== null && m.removedAt !== undefined) return [];

  const items = [
    {
      label: 'Change role',
      iconName: 'menu-edit',
      onSelect: async () => {
        const select = el('select', { class: 'input' }, ORG_ROLES.map((r) => el('option', { value: r, selected: r === m.role }, roleLabel(r))));
        const ok = await openModal({
          tone: 'indigo',
          iconName: 'modal-role',
          title: 'Change role',
          body: el(
            'div',
            { class: 'stack', style: 'gap:16px' },
            el('p', { class: 'modal__text' }, `${m.fullName} · ${m.email} · ${org}`, el('br'), 'Current role: ', roleChip(roleLabel(m.role))),
            el('div', { class: 'field' }, el('label', {}, 'New role'), select),
            el(
              'div',
              { class: 'modal__panel' },
              el('strong', {}, `Applies to ${org} only`),
              el('p', {}, 'Saved immediately. Their role in any other organisation is unaffected, and no email is sent. An organisation cannot be left without an OWNER.'),
            ),
          ),
          confirmLabel: 'Change role',
          canConfirm: () => select.value !== m.role,
          onConfirm: () => api.setRole(m.orgId, m.userId, select.value),
        });
        if (ok) onChanged(`${m.fullName} is now ${roleLabel(select.value)} in ${org}.`);
      },
    },
  ];

  if (!m.activated) {
    items.push({
      label: 'Resend invite',
      iconName: 'menu-users',
      onSelect: async () => {
        let r = null;
        const ok = await confirmDialog({
          tone: 'indigo',
          iconName: 'check-circle',
          title: `Resend the invite to ${m.email}?`,
          text: `A fresh join link for ${org} is issued and emailed. Their previous join link stops working — only the new one will work.`,
          confirmLabel: 'Resend invite',
          onConfirm: async () => {
            r = await api.resendInvite(m.orgId, m.userId);
          },
        });
        if (!ok || r === null) return;
        onCredentials?.(
          credentialsPanel({
            title: `New join link for ${r.email}`,
            user: { email: r.email, temporaryPassword: null, joinUrl: r.joinUrl, joinUrlExpiresAt: r.joinUrlExpiresAt },
            email: r.emailResult,
            note: 'Any previous join link for this person has been invalidated.',
          }),
        );
        onChanged(`New invite issued for ${r.email}.`);
      },
    });
  } else {
    const next = !m.membershipActive;
    items.push({
      label: next ? 'Reactivate in org' : 'Deactivate in org',
      iconName: next ? 'check-circle' : 'menu-deactivate',
      onSelect: async () => {
        const ok = await confirmDialog({
          tone: next ? 'green' : 'amber',
          iconName: next ? 'check-circle' : 'menu-deactivate',
          title: next ? `Reactivate ${m.fullName} in ${org}?` : `Deactivate ${m.fullName} in ${org}?`,
          text: next
            ? `Lets ${m.email} back into ${org} on their next request. Their role and join date are unchanged.`
            : `THIS ORGANISATION ONLY. Blocks ${m.email} from ${org} on their next request, without ending the membership — no re-invite needed to undo it. Their account and every other organisation they belong to are unaffected.`,
          confirmLabel: next ? 'Reactivate' : 'Deactivate in org',
          onConfirm: () => api.setMembershipStatus(m.orgId, m.userId, next),
        });
        if (ok) onChanged(`${m.fullName} ${next ? 'reactivated' : 'deactivated'} in ${org}.`);
      },
    });
  }

  items.push({
    label: 'Remove from organisation',
    iconName: 'menu-deactivate',
    danger: true,
    onSelect: async () => {
      const ok = await confirmDialog({
        tone: 'red',
        iconName: 'modal-question',
        title: `Remove ${m.fullName} from ${org}?`,
        text: `THIS ORGANISATION ONLY. Ends ${m.email}'s membership of ${org} and cancels any pending invite for it.`,
        alert: {
          title: 'Their account is kept',
          text: 'Their ConstructMind account, password and every other organisation they belong to are kept. Undo by adding them again.',
        },
        confirmLabel: 'Remove member',
        onConfirm: () => api.removeMember(m.orgId, m.userId),
      });
      if (ok) onChanged(`${m.fullName} removed from ${org}.`);
    },
  });

  return items;
}
